"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.findIntersections = findIntersections;
exports.removeCachedEntityBboxs = removeCachedEntityBboxs;
var _computeBbox = require("./computeBbox");
var _intersectEntities = require("./intersectEntities");
const symBbox = Symbol('bbox');

/**
 * Finds points of intersection between two contours.
 *
 * As part of this process, each entity's bounding box is pre-computed, if it does not already exist, and deleted before
 * the function returns. To improve performance in cases where this function will be run multiple times on the same
 * contours, you can pass `false` as the third argument to keep the cached bounding boxes instead of deleting them.
 *
 * @param {Array} contour The first contour, containing an array of entities
 * @param {Array} contour The second contour, containing an array of entities
 * @param {boolean} [autoRemoveCachedBbox=true] Whether to remove the cached bounding boxes of the entities before
 * returning. The default is true.
 */
function findIntersections(contour1, contour2, autoRemoveCachedBbox = true) {
  // TODO: An arc and a line, or two arcs, that are consecutive, could still potentially intersect like this:
  /*
         _____  
        /     \
  _____/_______\
      |
       \
  */
  // Do we really want to test every pair of consecutive entities for this? (It will be quick to see whether a supposed intersection is actually an endpoint.)

  // Precompute bounding boxes on each entity
  for (let e of contour1.entities) {
    if (!e[symBbox]) e[symBbox] = (0, _computeBbox.computeBbox)([e]);
  }
  for (let e of contour2.entities) {
    if (!e[symBbox]) e[symBbox] = (0, _computeBbox.computeBbox)([e]);
  }
  const intersectingPoints = [];
  for (let i = 0; i < contour1.entities.length; i++) {
    const e0 = contour1.entities[i];
    for (let j = 0; j < contour2.entities.length; j++) {
      const e1 = contour2.entities[j];

      // Check bounding boxes
      if (e0[symBbox].max.x < e1[symBbox].min.x) continue;
      if (e0[symBbox].min.x > e1[symBbox].max.x) continue;
      if (e0[symBbox].max.y < e1[symBbox].min.y) continue;
      if (e0[symBbox].min.y > e1[symBbox].max.y) continue;
      intersectingPoints.push(...(0, _intersectEntities.intersectEntities)(e0, e1));
    }
  }

  // The caller can choose to retain the bounding boxes, to be used in a subsequent call to this function.
  if (autoRemoveCachedBbox) {
    removeCachedEntityBboxs(contour1);
    removeCachedEntityBboxs(contour2);
  }
  return intersectingPoints;
}

/**
 * Removes the cached entity bounding boxes from the given contour, which were created during `findIntersections`.
 * @param {Contour} contour The contour whose entities should have their bounding boxes removed.
 */
function removeCachedEntityBboxs(contour) {
  for (let e of contour.entities) {
    delete e[symBbox];
  }
}